import * as React from 'react';
import { connect } from 'react-redux';
import { isNil } from 'lodash';

import MoreButton from 'components/buttons/more-button/MoreButton';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { isBrandsFocusedOrganization } from 'utils/organization';
import { APPROVAL_STATUSES, canFeedbackBeReset } from 'utils/contentApproval';
import { DELIVERABLE_TYPE_ZALANDO_OUTFIT } from 'utils/deliverables';
import { REALM_OUTFIT_STATUS_DRAFT, REALM_OUTFIT_STATUS_PENDING, getDownloadableOutfits } from 'utils/realm-outfits';

import { contentApprovalLinkSelector } from 'selectors/campaign';

import type { Deliverable, DeliverableAttachment, DeliverableAttachmentApprovalStatus } from 'types';

applyTranslations(require('./deliverable-media-context-menu.i18n.json'));

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  canEmpty: true
});

type OwnProps = {
  isInsights?: boolean,
  isPost?: boolean,
  isCcm?: boolean,
  canUpload?: boolean,
  width?: number,
  isBulk?: boolean,
  isEditMode?: boolean,
  deliverable: Deliverable,
  attachments: Array<DeliverableAttachment>,
  attachment?: DeliverableAttachment,
  hasClientAddedNewFeedback?: boolean,
  allowContentMove?: boolean,
  captionStatus?: DeliverableAttachmentApprovalStatus,
  onApplyClientFeedback?: (deliverable: Deliverable, attachmentId?: number | null) => void,
  onUploadModeToggled?: () => void,
  onResetApprovalStatus?: (attachments: Array<DeliverableAttachment>) => void,
  onDownloadAttachments?: (approvedOnly?: boolean | null) => void,
  onResetCaptionApprovalStatus?: () => void,
  onDeleteAttachments?: (deliverableAttachments: Array<DeliverableAttachment>) => void,
  onReplaceAttachment?: (deliverableAttachment: DeliverableAttachment) => void,
  onDeleteUrl?: () => void,
  onEditUrl?: () => void,
  onOpenMoveContent?: (showMoveContent: boolean) => void,
  onReleaseToClient?: (showMoveContent: boolean) => void,
  onDeleteOutfits?: () => void
};

type StateProps = {
  contentApprovalLink: string | undefined | null,
  isBrand: boolean,
  isBrandAgency: boolean
};

type Props = OwnProps & StateProps;

const msgKey = 'deliverable_media_context_menu';

class DeliverableMediaContextMenu extends React.PureComponent<Props> {
  handleMoveContent = () => {
    const { onOpenMoveContent } = this.props;

    if (onOpenMoveContent) {
      onOpenMoveContent(true);
    }
  };

  handleOpenClientApproval = () => {
    const { onReleaseToClient } = this.props;

    if (onReleaseToClient) {
      onReleaseToClient(true);
    }
  };

  handleUploadModeToggled = () => {
    const { onUploadModeToggled } = this.props;

    if (onUploadModeToggled) {
      onUploadModeToggled();
    }
  };

  handleDownloadFiles = (onlyApprovedFiles?: boolean) => {
    const { onDownloadAttachments } = this.props;

    if (onDownloadAttachments) {
      onDownloadAttachments(!!onlyApprovedFiles);
    }
  };

  handleDeleteFiles = () => {
    const {
      attachments,
      onDeleteAttachments
    } = this.props;

    if (attachments.length > 0) {
      const attachmentsWithoutFeedback = attachments.filter(a => a.approvalStatus !== 'approved' && a.approvalStatus !== 'declined');

      if (onDeleteAttachments) {
        onDeleteAttachments(attachmentsWithoutFeedback);
      }
    }
  };

  handleReplaceFile = () => {
    const { attachments, onReplaceAttachment } = this.props;

    if (onReplaceAttachment && attachments.length > 0) {
      onReplaceAttachment(attachments[0]);
    }
  };

  handleResetApprovalStatus = () => {
    const { attachments, onResetApprovalStatus, onResetCaptionApprovalStatus } = this.props;

    if (onResetCaptionApprovalStatus) {
      onResetCaptionApprovalStatus();
    } else if (onResetApprovalStatus) {
      onResetApprovalStatus(attachments);
    }
  };

  handleApplyClientFeedback = (attachmentId: number | null): void => {
    const { deliverable, onApplyClientFeedback } = this.props;


    if (onApplyClientFeedback) {
      onApplyClientFeedback(deliverable, attachmentId);
    }
  };

  getInsightsContextItems = () => {
    const {
      isBulk,
      isPost,
      isEditMode,
      attachments,
      onDeleteUrl,
      onEditUrl } = this.props;

    if (isPost) {
      if (!isEditMode) {
        return [{
          icon: 'approval-custom-fg',
          text: __t(`${msgKey}.context_menu.edit_content`),
          onClick: this.handleUploadModeToggled
        }];
      }

      return [{
        icon: 'trash-custom-fg',
        text: __t(`${msgKey}.context_menu.delete_url`),
        onClick: onDeleteUrl
      }, {
        type: 'separator'
      }, {
        icon: 'pencil-custom-fg',
        text: __t(`${msgKey}.context_menu.edit_url`),
        onClick: onEditUrl
      }];
    }

    return [
      ...insertIf(isEditMode && attachments.length, {
        icon: 'trash-custom-fg',
        text: __t(`${msgKey}.context_menu.${isBulk ? 'delete_all_files' : 'delete_file'}`),
        onClick: this.handleDeleteFiles
      }),
      ...insertIf(isEditMode, {
        type: 'separator'
      }),
      ...insertIf(isEditMode && attachments.length && !isBulk, {
        icon: 'add-or-delete-custom-fg',
        text: __t(`${msgKey}.context_menu.replace_file`),
        onClick: this.handleReplaceFile
      }),
      ...insertIf(isEditMode && attachments.length && !isBulk, {
        type: 'separator'
      }),
      ...insertIf(attachments.length, {
        icon: 'download-custom-fg',
        text: __t(`${msgKey}.context_menu.${isBulk ? 'download_all_files' : 'download_file'}`),
        onClick: this.handleDownloadFiles
      }),
      ...insertIf(attachments.length, {
        type: 'separator'
      }),
      ...insertIf(!isEditMode, {
        icon: 'add-or-delete-custom-fg',
        text: __t(`${msgKey}.context_menu.edit_content`),
        onClick: this.handleUploadModeToggled
      })
    ];
  };

  getContextItems = () => {
    const {
      isBulk,
      canUpload,
      isEditMode,
      attachments,
      attachment,
      allowContentMove,
      contentApprovalLink,
      hasClientAddedNewFeedback,
      deliverable,
      captionStatus,
      isBrand,
      isBrandAgency,
      isCcm,
      onDeleteOutfits
    } = this.props;
    const { contentApproval } = deliverable;
    const isCaptionApprovalRequired = captionStatus && captionStatus !== APPROVAL_STATUSES.NOT_REQUIRED;
    const isEditModeExtended = isEditMode || canUpload;
    const hasApprovedAttachments = (contentApproval?.assets || []).some(a => a.approvalStatus === APPROVAL_STATUSES.APPROVED && typeof a.id === 'number');
    const attachmentsWithFeedback = attachments.filter(a => a.approvalStatus === APPROVAL_STATUSES.APPROVED || a.approvalStatus === APPROVAL_STATUSES.DECLINED);
    const isAttachmentsReviewPending = attachmentsWithFeedback.length !== attachments.length;
    const isCaptionProvided = captionStatus && Object.values(APPROVAL_STATUSES).includes(captionStatus);
    const isViewMode = !isEditModeExtended;
    const canResetFeedback = isViewMode && canFeedbackBeReset(attachmentsWithFeedback, captionStatus, isCaptionApprovalRequired);

    if (deliverable.deliverableType === DELIVERABLE_TYPE_ZALANDO_OUTFIT) {
      if (!deliverable.outfits?.length) {
        return [];
      }

      const outfitsWithoutDraft = (deliverable.outfits || []).filter(outfit => outfit.status !== REALM_OUTFIT_STATUS_DRAFT);
      const outfitsWithPendingStatus = (deliverable.outfits || []).filter(outfit => outfit.status === REALM_OUTFIT_STATUS_PENDING);

      const downloadableOutfits = getDownloadableOutfits(deliverable.outfits || []);
      const downloadableApprovedOutfits = getDownloadableOutfits(deliverable.outfits || [], true);

      return [...insertIf(downloadableOutfits.length, {
        icon: 'download-custom-fg',
        text: __t(`${msgKey}.context_menu.download_all_files`),
        onClick: this.handleDownloadFiles
      }), ...insertIf(downloadableApprovedOutfits.length, {
        icon: 'download-custom-fg',
        text: __t(`${msgKey}.context_menu.download_approved_files`),
        onClick: () => this.handleDownloadFiles(true)
      }), ...insertIf(allowContentMove && outfitsWithoutDraft.length && isBrand && !isCcm, {
        icon: 'reposition-custom-fg',
        text: __t(`${msgKey}.context_menu.move_content`),
        onClick: this.handleMoveContent
      }), ...insertIf(outfitsWithPendingStatus.length && !isBrand, {
        icon: 'trash-custom-fg',
        text: __t(`${msgKey}.context_menu.delete_all_pending_outfits`),
        onClick: onDeleteOutfits
      })];
    }

    if (isViewMode && !(attachments.length || isCaptionProvided)) {
      return [{
        icon: 'add-or-delete-custom-fg',
        text: __t(`${msgKey}.context_menu.edit_content`),
        onClick: this.handleUploadModeToggled
      }];
    }

    const clientPageContent = [
      ...insertIf(isViewMode && attachments.length && isBrandAgency, {
        icon: 'approval-custom-fg',
        text: __t(`${msgKey}.context_menu.release_content`),
        onClick: this.handleOpenClientApproval
      }),
      ...insertIf(isViewMode && contentApprovalLink && isBrandAgency, {
        type: 'link',
        icon: 'link-external-custom-fg',
        text: __t(`${msgKey}.context_menu.open_client_page`),
        link: contentApprovalLink,
        openInNewTab: true
      }),
      ...insertIf(isViewMode && hasClientAddedNewFeedback, {
        icon: 'rotation-arrows-custom-fg',
        text: __t(`${msgKey}.context_menu.apply_client_feedback`),
        onClick: () => this.handleApplyClientFeedback(attachment ? Number(attachment.id) : null)
      })
    ];

    return [
      ...insertIf(isEditModeExtended && isAttachmentsReviewPending, {
        icon: 'trash-custom-fg',
        text: __t(`${msgKey}.context_menu.${isBulk ? 'delete_all_files' : 'delete_file'}`),
        onClick: this.handleDeleteFiles
      }),
      ...insertIf(isEditModeExtended && isAttachmentsReviewPending, {
        type: 'separator'
      }),
      ...clientPageContent,
      ...insertIf(isViewMode && clientPageContent.length, {
        type: 'separator'
      }),
      ...insertIf(canResetFeedback, {
        icon: 'rotate-left-custom-fg',
        text: __t(`${msgKey}.context_menu.${isBulk ? 'reset_approval_all' : 'reset_approval'}`),
        onClick: this.handleResetApprovalStatus
      }),
      ...insertIf(canResetFeedback, {
        type: 'separator'
      }),
      ...insertIf(attachments.length, {
        icon: 'download-custom-fg',
        text: __t(`${msgKey}.context_menu.${isBulk ? 'download_all_files' : 'download_file'}`),
        onClick: this.handleDownloadFiles
      }),
      ...insertIf(isBulk && hasApprovedAttachments, {
        icon: 'download-custom-fg',
        text: __t(`${msgKey}.context_menu.download_approved_files`),
        onClick: () => this.handleDownloadFiles(true)
      }),
      ...insertIf(isViewMode && allowContentMove && attachments.length, {
        type: 'separator'
      }),
      ...insertIf(isViewMode && allowContentMove && attachments.length, {
        icon: 'reposition-custom-fg',
        text: __t(`${msgKey}.context_menu.move_content`),
        onClick: this.handleMoveContent
      }),
      ...insertIf(attachments.length, {
        type: 'separator'
      }),
      ...insertIf(isViewMode, {
        icon: 'add-or-delete-custom-fg',
        text: __t(`${msgKey}.context_menu.edit_content`),
        onClick: this.handleUploadModeToggled
      })
    ];
  };

  renderContextMenu(): React.ReactNode {
    const { deliverable, width, isInsights, isBulk } = this.props;

    if (!deliverable) {
      return null;
    }

    const items = isInsights ? this.getInsightsContextItems() : this.getContextItems();

    if (items[items.length - 1]?.type === 'separator') {
      items.pop();
    }

    return (
      <MoreButtonWithMenu
        width={width || 20}
        contextMenuProps={{ items }}
        disabled={!items.length}
        iconHeight={isBulk && 24}
        iconWidth={isBulk && 6}
      />
    );
  }

  render(): React.ReactNode {
    return this.renderContextMenu();
  }
}

const mapStateTopProps = (state): StateProps => ({
  contentApprovalLink: contentApprovalLinkSelector(state),
  isBrand: isBrandsFocusedOrganization(state?.organizations?.currentOrganization),
  isBrandAgency: !isNil(state?.organizations?.currentOrganization?.agencyOrganizationId)
});

export default connect(mapStateTopProps)(DeliverableMediaContextMenu);
